import { isHTTPError } from 'ky';

const UNAUTHORIZED_STATUS = 401;
const SIGN_IN_PATH = '/sign-in';
const REDIRECT_URL_QUERY_KEY = 'redirect_url';

/**
 * Clerk has no session left to mint a token from, so the request never went out. Thrown instead of
 * sending an unauthenticated request the API can only answer with a second 401.
 */
export class ExpiredSessionError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'ExpiredSessionError';
  }
}

/**
 * A session that ran out while the tab sat idle is a state the product has an answer for — the
 * sign-in redirect below — not a defect. Reporting it drowned the Sentry project: 2.4k events across
 * 16 groups, every one of them a student coming back to a tab they left open overnight.
 */
export function isExpiredSessionError(error: unknown): boolean {
  if (error instanceof ExpiredSessionError) {
    return true;
  }

  return isHTTPError(error) && error.response.status === UNAUTHORIZED_STATUS;
}

/**
 * A hard navigation, not a router push: the stale Clerk client and the whole query cache have to die
 * with the old session. `redirect_url` is what the middleware already reads to put the student back
 * on the step they were on — and if the cookie outlived the client token, `authPageGuard` bounces
 * them straight back there with a freshly minted one instead of asking for credentials.
 */
export function redirectToSignIn(): void {
  if (window.location.pathname.startsWith(SIGN_IN_PATH)) {
    return;
  }

  const signInUrl = new URL(SIGN_IN_PATH, window.location.origin);
  signInUrl.searchParams.set(REDIRECT_URL_QUERY_KEY, `${window.location.pathname}${window.location.search}`);

  window.location.assign(signInUrl.toString());
}
